import { apiJson } from '../api';
import { readerEvent } from './readerTelemetry';
import { decodeJwtPayload, getAccessToken } from '../auth';
import {
  clearAnonymousInterestsCookie,
  getAnonymousInterestsCookie,
  type AnonymousInterestPref,
} from './anonymousInterestsCookie';

interface LocalFeedAdd {
  description: string;
}
interface LocalPreferences {
  interests: AnonymousInterestPref[];
  feedAdd?: LocalFeedAdd;
  userId?: string;
}
const STORAGE_KEY = 'tldr_local_reader_preferences';

export function localPreferencesAccountId(): string | null {
  return decodeJwtPayload(getAccessToken() ?? '')?.sub ?? null;
}

function read(): LocalPreferences | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || !Array.isArray(value.interests)) return null;
    if (value.userId !== undefined && typeof value.userId !== 'string') return null;
    if (value.feedAdd && (typeof value.feedAdd.description !== 'string' || !value.feedAdd.description.trim())) return null;
    return value;
  } catch {
    return null;
  }
}

export function rememberLocalFeedAdd(feedAdd: LocalFeedAdd): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ interests: [], feedAdd }));
}

function pendingFor(userId: string): LocalPreferences | null {
  const saved = read();
  // A failed transfer belongs to the account that first attempted it.
  if (saved?.userId) return saved.userId === userId ? saved : null;
  const interests = getAnonymousInterestsCookie() ?? [];
  return saved || interests.length ? { ...saved, interests } : null;
}

export function hasLocalReaderPreferences(userId: string): boolean {
  return pendingFor(userId) !== null;
}

export function discardLocalReaderPreferences(userId: string): void {
  if (!pendingFor(userId)) return;
  sessionStorage.removeItem(STORAGE_KEY);
  clearAnonymousInterestsCookie();
}

export async function applyLocalReaderPreferences(userId: string): Promise<void> {
  const pending = pendingFor(userId);
  if (!pending) return;
  const serialized = JSON.stringify({ ...pending, userId });
  // Bind and persist before sending. A lost response or reload retries exactly
  // this payload, never an unrelated account's or a newer anonymous selection.
  sessionStorage.setItem(STORAGE_KEY, serialized);
  await apiJson('/api/v1/reader/searches/local-preferences', {
    method: 'POST',
    json: {
      interests: pending.interests,
      feed_add: pending.feedAdd ? { description: pending.feedAdd.description } : undefined,
    },
  });
  if (localPreferencesAccountId() !== userId || sessionStorage.getItem(STORAGE_KEY) !== serialized) return;
  sessionStorage.removeItem(STORAGE_KEY);
  if (pending.feedAdd) readerEvent('feed_add', { method: 'add_button', action: 'create' });
  if (JSON.stringify(getAnonymousInterestsCookie() ?? []) === JSON.stringify(pending.interests)) {
    clearAnonymousInterestsCookie();
  }
}
